import { useState } from 'react';
import { Badge, Button, DataTable } from 'lift-ui';
import type { TableAction, TableColumn } from 'lift-ui';
import { Pause, Play, RefreshCw } from 'lucide-react';
import { usePauseQueue, useQueues, useResumeQueue, useJobsAccess } from '../hooks';
import type { Queue } from '../types';
import { formatCount } from '../utils';
import { ConfirmDialog } from './ConfirmDialog';

export const QueuesTab = ({ queuePausingSupported }: { queuePausingSupported: boolean }) => {
  const { canManage } = useJobsAccess();
  const { data, isLoading, isFetching, refetch } = useQueues();
  const pauseQueue = usePauseQueue();
  const resumeQueue = useResumeQueue();
  const [queueToPause, setQueueToPause] = useState<Queue | null>(null);

  const isMutating = pauseQueue.isPending || resumeQueue.isPending;

  const columns: TableColumn<Queue>[] = [
    { key: 'name', header: 'Queue', accessor: 'name' },
    {
      key: 'size',
      header: 'Pending jobs',
      accessor: 'size',
      align: 'right',
      render: (_value, row) => formatCount(row.size),
    },
    {
      key: 'paused',
      header: 'State',
      accessor: 'paused',
      render: (_value, row) =>
        row.paused ? <Badge variant="warning">Paused</Badge> : <Badge variant="success">Active</Badge>,
    },
  ];

  const actions: TableAction<Queue>[] = [];

  if (canManage && queuePausingSupported) {
    actions.push({
      label: 'Pause',
      icon: <Pause size={14} />,
      variant: 'outline',
      disabled: (row) => row.paused || isMutating,
      onClick: (row) => setQueueToPause(row),
    });
    actions.push({
      label: 'Resume',
      icon: <Play size={14} />,
      variant: 'outline',
      disabled: (row) => !row.paused || isMutating,
      onClick: (row) => resumeQueue.mutate(row.id),
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      <DataTable<Queue>
        data={data ?? []}
        columns={columns}
        actions={actions}
        showActions={actions.length > 0}
        loading={isLoading}
        emptyMessage="No queues are in use"
        stickyHeader
      />

      <ConfirmDialog
        open={Boolean(queueToPause)}
        title={`Pause the ${queueToPause?.name ?? ''} queue?`}
        description="Workers stop picking up jobs from this queue until it is resumed. Jobs already running finish normally."
        confirmLabel="Pause queue"
        destructive
        pending={pauseQueue.isPending}
        onCancel={() => setQueueToPause(null)}
        onConfirm={() => {
          if (queueToPause) {
            pauseQueue.mutate(queueToPause.id, { onSettled: () => setQueueToPause(null) });
          }
        }}
      />
    </div>
  );
};

export default QueuesTab;
